/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { Modal, Grid } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import { toast } from "react-toastify";
import api from "utils/api";

function EditAttendanceModal({ open, onClose, attendance, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    topicCovered: "",
    type: "",
    homeWorkStatus: "",
    attendanceTime: "",
  });

  useEffect(() => {
    if (attendance && open) {
      const attendanceTime = attendance.attendanceTime
        ? new Date(attendance.attendanceTime).toISOString().slice(0, 16)
        : "";

      setFormData({
        topicCovered: attendance.topicCovered || "",
        type: attendance.type || "",
        homeWorkStatus: attendance.homeWorkStatus || "",
        attendanceTime: attendanceTime,
      });
    }
  }, [attendance, open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDropdownChange = (name, value) => {
    // Extract the actual value if it's an event object
    const actualValue = value?.target ? value.target.value : value;

    setFormData((prev) => ({
      ...prev,
      [name]: actualValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.topicCovered?.trim()) {
      toast.error("Topic covered is required");
      return;
    }

    setLoading(true);

    try {
      const updatePayload = {
        attendanceId: attendance.id || attendance._id,
        topicCovered: formData.topicCovered.trim(),
      };

      // Only add type if it's a valid string value
      if (formData.type && typeof formData.type === "string") {
        updatePayload.type = formData.type;
      }

      // Only add homeWorkStatus if it's a valid string value
      if (formData.homeWorkStatus && typeof formData.homeWorkStatus === "string") {
        updatePayload.homeWorkStatus = formData.homeWorkStatus;
      }

      if (formData.attendanceTime) {
        updatePayload.attendanceTime = new Date(formData.attendanceTime).toISOString();
      }

      const response = await api.put(
        `/attendance/${attendance.id || attendance._id}`,
        updatePayload
      );

      if (response.data.success) {
        toast.success("Attendance updated successfully");
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error(error.response?.data?.message || "Failed to update attendance");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      topicCovered: "",
      type: "",
      homeWorkStatus: "",
      attendanceTime: "",
    });
    onClose();
  };

  const typeOptions = [
    { label: "Regular", value: "REGULAR" },
    { label: "Extra", value: "EXTRA" },
    { label: "Recovery", value: "RECOVERY" },
  ];

  const homeWorkStatusOptions = [
    { label: "Done", value: "DONE" },
    { label: "Incomplete", value: "INCOMPLETE" },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <ArgonBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "80%", md: "600px" },
          background: "linear-gradient(195deg, #49a3f1, #1A73E8)",
          borderRadius: "20px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
        }}
      >
        <ArgonBox
          sx={{
            background: "white",
            borderRadius: "20px",
            m: 2,
            p: 3,
          }}
        >
          <ArgonTypography variant="h5" fontWeight="bold" mb={3}>
            Edit Attendance
          </ArgonTypography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Topic Covered *
                  </ArgonTypography>
                </ArgonBox>
                <ArgonInput
                  type="text"
                  name="topicCovered"
                  value={formData.topicCovered}
                  onChange={handleInputChange}
                  placeholder="Enter topic covered"
                  fullWidth
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Attendance Type
                  </ArgonTypography>
                </ArgonBox>
                <Dropdown
                  options={typeOptions}
                  value={formData.type}
                  onChange={(value) => handleDropdownChange("type", value)}
                  placeholder="Select type"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Homework Status
                  </ArgonTypography>
                </ArgonBox>
                <Dropdown
                  options={homeWorkStatusOptions}
                  value={formData.homeWorkStatus}
                  onChange={(value) => handleDropdownChange("homeWorkStatus", value)}
                  placeholder="Select status"
                />
              </Grid>

              <Grid item xs={12}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Attendance Date & Time
                  </ArgonTypography>
                </ArgonBox>
                <ArgonInput
                  type="datetime-local"
                  name="attendanceTime"
                  value={formData.attendanceTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
            </Grid>

            <ArgonBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <ArgonButton variant="outlined" color="secondary" onClick={handleClose}>
                Cancel
              </ArgonButton>
              <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                {loading ? "Updating..." : "Update Attendance"}
              </ArgonButton>
            </ArgonBox>
          </form>
        </ArgonBox>
      </ArgonBox>
    </Modal>
  );
}

export default EditAttendanceModal;
